import { Box, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { capitalizar, getInicialesNombre, ordenarLista } from '../../utils/UtilesComun';
import BotonListasConJugador from '../botones/BotonListasConJugador';
import BotonObservacionesLista from '../botones/BotonObservacionesLista';
import { AvatarApp, TableCellApp, TableModuleApp, TypographyApp, TypographyTableApp } from '../custom/CustomComponents';

const columnsFull = [
  { field: 'observaciones', label: 'Listas y Obs.', width: '10%', tooltip: 'Añadir a listas u observaciones' },
  { field: 'nombre', label: 'Nombre', width: '32%', tooltip: 'Nombre' },
  { field: 'ano', label: 'Año', width: '8%', tooltip: 'Año de nacimiento' },
  { field: 'club', label: 'Club', width: '30%', tooltip: 'Club actual' },
  { field: 'demarcacion', label: 'Demarc.', width: '20%', tooltip: 'Demarcación' },
  { field: 'creador', label: 'Cread.', width: '6%', tooltip: 'Usuario creador' },
];

const columnsCompact = [
  { field: 'nombre', label: 'Nombre', width: '75%', tooltip: 'Nombre del jugador' },
  { field: 'ano', label: 'Año', width: '25%', tooltip: 'Año de nacimiento' },
];

/**
 * Fila de la tabla de jugadores manuales.
 */
const FilaJugadorManual = ({ jugador, columns, compact = false, idUsuario }) => {
  const navigate = useNavigate();
  const [observaciones, setObservaciones] = useState(jugador.observaciones || []);

  return (
    <TableRow
      key={jugador.idJugador}
      hover
      onClick={() => navigate(`/jugador/${jugador.idJugador}`)}
      sx={{ cursor: 'pointer' }}
    >
      {columns.map((col) => {
        if (col.field === 'observaciones') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <BotonListasConJugador idJugador={jugador.idJugador} enLista={true} />
                <BotonObservacionesLista
                  idJugador={jugador.idJugador}
                  observaciones={observaciones}
                  onUpdate={(_, updatedObs) => setObservaciones(updatedObs)}
                />
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'nombre') {
          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, px: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              <Tooltip title={capitalizar(jugador.nombre)} arrow placement="top">
                <TypographyApp
                  sx={{
                    fontWeight: compact ? 400 : 600,
                    fontSize: compact ? '0.78rem' : '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {capitalizar(jugador.nombre)}
                </TypographyApp>
              </Tooltip>
            </TableCellApp>
          );
        }

        if (col.field === 'ano') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, px: 0.5, textAlign: 'center' }}>
              <TypographyApp sx={{ fontSize: '0.78rem', fontWeight: 400, color: 'text.secondary' }}>
                {jugador.ano || '-'}
              </TypographyApp>
            </TableCellApp>
          );
        }

        if (col.field === 'club') {
          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, px: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              <Tooltip title={capitalizar(jugador.clubActual || '')} arrow placement="top">
                <TypographyApp
                  sx={{
                    fontSize: '0.78rem',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {capitalizar(jugador.clubActual || '-') || '-'}
                </TypographyApp>
              </Tooltip>
            </TableCellApp>
          );
        }

        if (col.field === 'demarcacion') {
          return (
            <TypographyTableApp key={col.field} sx={{ py: 0.5 }}>
              {jugador.demarcacion || '-'}
            </TypographyTableApp>
          );
        }

        if (col.field === 'creador') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5 }}>
              {jugador.nombreUsuarioCreador ? (
                <Tooltip title={jugador.nombreUsuarioCreador} arrow>
                  <AvatarApp size={20} esPropio={jugador.idUsuarioCreador === idUsuario} sx={{ mr: 0.5 }}>
                    {getInicialesNombre(jugador.nombreUsuarioCreador)}
                  </AvatarApp>
                </Tooltip>
              ) : (
                '-'
              )}
            </TableCellApp>
          );
        }

        return <TableCellApp key={col.field}>{jugador[col.field]}</TableCellApp>;
      })}
    </TableRow>
  );
};

FilaJugadorManual.propTypes = {
  jugador: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  compact: PropTypes.bool,
  idUsuario: PropTypes.number,
};

const JugadoresManualesUsuario = ({ jugadores, idUsuario, compact = false }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(compact ? 5 : 10);
  const [sort, setSort] = useState({ field: 'nombre', direction: 'asc' });

  const columns = compact ? columnsCompact : columnsFull;

  const sortedJugadores = useMemo(() => {
    return ordenarLista(jugadores || [], sort);
  }, [jugadores, sort]);

  if (!jugadores || jugadores.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>
        No has creado ningún jugador manualmente.
      </Box>
    );
  }

  return (
    <TableModuleApp
      noHeader
      noBorder
      data={sortedJugadores.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
      columns={columns}
      sort={sort}
      setSort={setSort}
      renderRow={(jugador) => (
        <FilaJugadorManual
          key={jugador.idJugador}
          jugador={jugador}
          columns={columns}
          compact={compact}
          idUsuario={idUsuario}
        />
      )}
      hasPagination
      page={page}
      setPage={setPage}
      rowsPerPage={rowsPerPage}
      setRowsPerPage={setRowsPerPage}
      rowsPerPageOptions={compact ? [5, 10, 20] : [10, 25, 50]}
      totalCount={sortedJugadores.length}
    />
  );
};

JugadoresManualesUsuario.propTypes = {
  jugadores: PropTypes.array,
  idUsuario: PropTypes.number,
  compact: PropTypes.bool,
};

export default JugadoresManualesUsuario;
